import JSZip from 'jszip';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { StructureSet } from '~/domain/patient/structureset';
import { isDerivedDose } from '~/domain/dose/derivedDose';
import { localization } from '~/i18n';
import { handleError } from '~/utils/errors';
import { enqueueSnackbar } from '~/utils/notifications';
import { sanitizeDicomLabel } from './dicomFilename';

export function saveBlobToFile(blob: Blob, filename: string) {
    try {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    } catch (error) {
        handleError(error, `Failed saving file: ${filename}`);
    }
}

export async function saveBlobAsDicom(
    label: string,
    blob: Blob
): Promise<void> {
    try {
        const safeLabel = sanitizeDicomLabel(label);
        const filename = `${safeLabel}.dcm`;
        saveBlobToFile(blob, filename);
    } catch (error) {
        handleError(error, `Error saving ${label} to disk`);
    }
}

// A dose made in this session is never written out: it is not saved, and its file would carry no patient or study.
function refuseDerivedDose(): void {
    enqueueSnackbar(localization.t('dose.derived.notDownloadable'), {
        variant: 'warning',
    });
}

export async function downloadDoseToDisk(
    doseMatrix: DoseMatrix
): Promise<void> {
    if (isDerivedDose(doseMatrix)) {
        refuseDerivedDose();
        return;
    }
    const safeLabel = sanitizeDicomLabel(
        doseMatrix.plan?.label ?? doseMatrix.dicomUid
    );

    try {
        const doseFilename = `RTDOSE_${safeLabel}.dcm`;
        const planFilename = `RTPLAN_${safeLabel}.dcm`;

        const doseBlob = await doseMatrix.dicomDose?.getAsDicomBlob();
        if (doseBlob) {
            saveBlobToFile(doseBlob, doseFilename);
        }

        const planBlob = await doseMatrix.plan?.dicom?.getAsDicomBlob();
        if (planBlob) {
            saveBlobToFile(planBlob, planFilename);
        }
    } catch (error) {
        handleError(error, `Couldn't download dose: ${safeLabel}`);
    }
}

export async function downloadImageToDisk(image: Image3D): Promise<void> {
    try {
        const blobs = Array.from(image.getSlicesAsDicomBlobs());
        const zip = new JSZip();

        // Adding blobs in a modality + series UID subdirectory
        const dir = `${image.seriesMetadata.modality}_${image.dicomUid}`;
        blobs.forEach((blob, index) => {
            zip.file(`${dir}/${index}.dcm`, blob);
        });

        const zipBlob = await zip.generateAsync({
            type: 'blob',
            compression: 'DEFLATE',
        });

        saveBlobToFile(
            zipBlob,
            `${image.seriesMetadata.modality}_${image.dicomUid}.zip`
        );
    } catch (error) {
        handleError(
            error,
            `Couldn't download image: ${image.seriesMetadata.seriesDescription}`
        );
    }
}

export type ExportItems = {
    images?: Image3D[];
    doses?: DoseMatrix[];
    structureSets?: StructureSet[];
    registrations?: RigidRegistration[];
};

export async function downloadItemsToDisk(
    {
        images = [],
        doses = [],
        structureSets = [],
        registrations = [],
    }: ExportItems,
    filename: string
): Promise<void> {
    if (doses.some(isDerivedDose)) {
        refuseDerivedDose();
        return;
    }
    try {
        const zip = new JSZip();

        for (const image of images) {
            const blobs = Array.from(image.getSlicesAsDicomBlobs());
            blobs.forEach((blob, index) => {
                const filename = `${image.seriesMetadata.modality}_${image.dicomUid}_${index}.dcm`;
                zip.file(filename, blob);
            });
        }

        for (const doseMatrix of doses) {
            const safeLabel = sanitizeDicomLabel(
                doseMatrix.plan?.label ?? doseMatrix.dicomUid
            );

            const doseBlob = await doseMatrix.dicomDose?.getAsDicomBlob();
            if (doseBlob) {
                const doseFilename = `RD_${safeLabel}.dcm`;
                zip.file(doseFilename, doseBlob);
            }

            const planBlob = await doseMatrix.plan?.dicom?.getAsDicomBlob();
            if (planBlob) {
                const planFilename = `RP_${safeLabel}.dcm`;
                zip.file(planFilename, planBlob);
            }
        }

        for (const structureSet of structureSets) {
            const blob = await structureSet.dicomStructureSet.getAsDicomBlob();
            if (blob) {
                const safeLabel = sanitizeDicomLabel(
                    structureSet.dicomStructureSet.structureSetLabel
                );
                const filename = `${safeLabel}.dcm`;
                zip.file(filename, blob);
            }
        }

        for (const rigidRegistration of registrations) {
            const blob = await rigidRegistration.dicomParent.getAsDicomBlob();
            if (blob) {
                const safeLabel = sanitizeDicomLabel(
                    rigidRegistration.contentLabel
                );
                const filename = `${safeLabel}.dcm`;
                zip.file(filename, blob);
            }
        }

        // Generate the ZIP file
        const zipBlob = await zip.generateAsync({
            type: 'blob',
            compression: 'DEFLATE',
        });

        // Download the ZIP file
        saveBlobToFile(zipBlob, `${filename}.zip`);
    } catch (error) {
        handleError(error, `Couldn't download selected items.`);
    }
}
